import React from "react";

/**
 * Markdown SEGURO para las respuestas del chat: negritas, cursivas, `código`, listas (hasta 4 niveles de sangría), TABLAS (GFM), «---»,
 * títulos (# a ####), citas (> nota), enlaces https y bloques ``` — todo como elementos de React. NUNCA se inserta HTML crudo: una
 * etiqueta como <script> se muestra como texto (y no se instala ninguna librería de Markdown: no hay nada nuevo que pueda traer HTML).
 */

export const CODE_CHIP =
  "font-['JetBrains_Mono',ui-monospace,monospace] text-[12px] px-1.5 py-0.5 rounded-md bg-foreground/[0.07] text-foreground/90 align-baseline";
const SAFE_URL = /^https?:\/\/[^\s]+$/i;
const INLINE = /(`[^`\n]+`)|(\*\*(?!\s)[^*\n]*[^*\s]\*\*)|(\*(?![\s*])[^*\n]*[^*\s]\*)|(\[[^\]\n]+\]\([^)\s]+\))/;

export function renderInline(text, keyPrefix = "i") {
  const nodes = [];
  let rest = text || "";
  let n = 0;
  while (rest) {
    const m = INLINE.exec(rest);
    if (!m) {
      nodes.push(rest);
      break;
    }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${n++}`;
    if (tok.startsWith("`")) {
      nodes.push(<code key={key} className={CODE_CHIP}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("**")) {
      nodes.push(<strong key={key} className="font-bold text-foreground/95">{renderInline(tok.slice(2, -2), key)}</strong>);
    } else if (tok.startsWith("*")) {
      nodes.push(<em key={key}>{renderInline(tok.slice(1, -1), key)}</em>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (link && SAFE_URL.test(link[2])) {
        nodes.push(
          <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer nofollow" className="text-cyan-700 dark:text-[#80fff9] hover:underline underline-offset-2">
            {link[1]}
          </a>,
        );
      } else {
        nodes.push(tok); // enlace no seguro (javascript:, data:…): se muestra como texto
      }
    }
    rest = rest.slice(m.index + tok.length);
  }
  return nodes;
}

function withBreaks(lines, keyPrefix) {
  const out = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push(<br key={`${keyPrefix}-br-${i}`} />);
    out.push(...renderInline(line, `${keyPrefix}-${i}`));
  });
  return out;
}

const BULLET = /^(\s*)([-*•])\s+(.*)$/;
const NUMBER = /^(\s*)(\d{1,3})[.)]\s+(.*)$/;
const HR = /^\s*([-*_])\s*(\1\s*){2,}$/;
const HEADING = /^\s{0,3}(#{1,4})\s+(.*?)\s*#*\s*$/;
const FENCE = /^\s*```/;
const TABLE_SEP = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const MAX_DEPTH = 3;

// ---- tablas (GFM): encabezado | separador con --- | filas. Las celdas aceptan negritas, `código` y enlaces seguros; «\|» es una barra dentro de la celda.
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells = [];
  let cur = "";
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "|";
      i += 1;
    } else if (s[i] === "|") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += s[i];
    }
  }
  cells.push(cur.trim());
  return cells;
}

function isTableStart(lines, i) {
  if (i + 1 >= lines.length || !lines[i].includes("|") || !TABLE_SEP.test(lines[i + 1])) return false;
  if (!lines[i + 1].includes("|") && !/-{3,}/.test(lines[i + 1])) return false; // sin barras, la raya necesita 3 guiones (si no, es una viñeta suelta)
  return splitRow(lines[i]).length === splitRow(lines[i + 1]).length; // «a | b» sobre un «---» suelto es un texto y una raya, no una tabla
}

function TableBlock({ head, align, rows, keyPrefix }) {
  const cls = (j) => (align[j] === "center" ? "text-center" : align[j] === "right" ? "text-right" : "text-left");
  return (
    <div data-testid="md-table" className="my-4 overflow-x-auto">
      <table className="w-full text-base border-collapse table-auto">
        <thead>
          <tr>
            {head.map((h, j) => (
              <th key={j} className={`px-4 py-2 font-semibold text-foreground/80 align-top ${cls(j)}`}>{renderInline(h, `${keyPrefix}-h${j}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-foreground/35 first:border-t-0">
              {head.map((_, j) => (
                <td key={j} className={`px-4 py-2 align-top break-words leading-7 ${cls(j)} ${j === 0 ? "font-semibold text-foreground/95" : "text-foreground/80"}`}>
                  {renderInline(row[j] ?? "", `${keyPrefix}-${i}-${j}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- listas: la sangría (2 espacios = un nivel, hasta 4 niveles) arma el árbol; una línea sangrada sin viñeta continúa el punto anterior.
function listItems(lines, start) {
  const flat = [];
  let i = start;
  const ordered = NUMBER.test(lines[start]);
  while (i < lines.length) {
    const b = BULLET.exec(lines[i]);
    const nm = NUMBER.exec(lines[i]);
    const m = b || nm;
    if (!m) {
      if (flat.length && /^\s{2,}\S/.test(lines[i]) && !HR.test(lines[i]) && !isTableStart(lines, i)) {
        flat[flat.length - 1].text += " " + lines[i].trim();
        i += 1;
        continue;
      }
      break;
    }
    const spaces = m[1].replace(/\t/g, "  ").length;
    if (spaces < 2 && ((nm && !ordered) || (b && ordered))) break; // otro tipo de lista en el nivel de arriba: es una lista nueva
    const prev = flat.length ? flat[flat.length - 1].depth : -1;
    flat.push({ text: m[3], ordered: !!nm, depth: Math.min(Math.floor(spaces / 2), MAX_DEPTH, prev + 1) });
    i += 1;
  }
  const tree = [];
  const stack = [];
  flat.forEach((it) => {
    const node = { text: it.text, ordered: it.ordered, children: [] };
    while (stack.length > it.depth) stack.pop();
    if (it.depth === 0) tree.push(node);
    else stack[it.depth - 1].children.push(node);
    stack[it.depth] = node;
  });
  return { items: tree, ordered, next: i };
}

// Función normal (no un componente que se llama a sí mismo): el plugin de edición visual de la compilación de pruebas se enreda con componentes recursivos.
function renderList(items, ordered, depth, keyPrefix) {
  const Tag = ordered ? "ol" : "ul";
  const marker = ordered ? "list-decimal" : depth === 0 ? "list-disc" : depth === 1 ? "list-[circle]" : "list-[square]";
  return (
    <Tag key={keyPrefix} className={`${marker} pl-5 ${depth === 0 ? "my-3" : "mt-1.5"} space-y-1.5 marker:text-foreground/50`}>
      {items.map((it, i) => (
        <li key={`${keyPrefix}-${i}`} className="leading-7 pl-1">
          {renderInline(it.text, `${keyPrefix}-${i}`)}
          {it.children.length > 0 && renderList(it.children, it.children[0].ordered, depth + 1, `${keyPrefix}-${i}`)}
        </li>
      ))}
    </Tag>
  );
}

const HEADING_STYLE = {
  1: ["h2", "text-2xl leading-8 tracking-[-0.025em] mt-6 -mb-1"],
  2: ["h2", "text-2xl leading-8 tracking-[-0.025em] mt-6 -mb-1"],
  3: ["h3", "text-xl leading-7 tracking-[-0.02em] mt-5 -mb-0.5"],
  4: ["h4", "text-base leading-7 mt-4"],
};

export function Markdown({ text, inline = false }) {
  const source = String(text ?? "").replace(/\r\n/g, "\n");
  if (inline) return <>{withBreaks(source.split("\n"), "m")}</>;
  const lines = source.split("\n");
  const blocks = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (FENCE.test(line)) {
      const code = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) code.push(lines[i++]);
      i += 1;
      blocks.push(
        <pre key={`b${k++}`} className="my-3 rounded-lg border border-border bg-zinc-950 px-3 py-2 text-sm text-zinc-100 font-mono overflow-x-auto">
          <code>{code.join("\n")}</code>
        </pre>,
      );
    } else if (isTableStart(lines, i)) {
      const head = splitRow(lines[i]);
      const align = splitRow(lines[i + 1]).map((c) => (c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : "left"));
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) rows.push(splitRow(lines[i++]));
      blocks.push(<TableBlock key={`b${k++}`} head={head} align={align} rows={rows} keyPrefix={`t${k}`} />);
    } else if (HR.test(line)) {
      blocks.push(<hr key={`b${k++}`} className="border-foreground/35 my-6" />);
      i += 1;
    } else if (HEADING.test(line)) {
      const [, hashes, content] = HEADING.exec(line);
      const [Tag, size] = HEADING_STYLE[hashes.length];
      blocks.push(
        <Tag key={`b${k++}`} data-testid={`md-h${hashes.length}`} className={`${size} first:mt-0 font-semibold text-foreground/95`}>
          {renderInline(content, `h${k}`)}
        </Tag>,
      );
      i += 1;
    } else if (/^\s*>/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) quote.push(lines[i++].replace(/^\s*>\s?/, ""));
      blocks.push(
        <div key={`b${k++}`} data-testid="md-note" className="my-3 border-l-4 border-amber-500/60 pl-4 italic text-foreground/75">
          {withBreaks(quote, `q${k}`)}
        </div>,
      );
    } else if (BULLET.test(line) || NUMBER.test(line)) {
      const { items, ordered, next } = listItems(lines, i);
      blocks.push(renderList(items, ordered, 0, `l${k++}`));
      i = next;
    } else {
      const para = [];
      while (
        i < lines.length && lines[i].trim() && !FENCE.test(lines[i]) && !HR.test(lines[i]) && !HEADING.test(lines[i]) &&
        !/^\s*>/.test(lines[i]) && !BULLET.test(lines[i]) && !NUMBER.test(lines[i]) && !isTableStart(lines, i)
      ) para.push(lines[i++]);
      blocks.push(<p key={`b${k++}`} className="my-3 text-base leading-7">{withBreaks(para, `p${k}`)}</p>);
    }
  }
  return <>{blocks}</>;
}
